import type { ReceiveConfirmScope } from '../model/confirm-types';
import type { ReceiveLocationType, ReceiveMovementType } from '../model/types';

export const RECEIVE_LOCATION_TYPE: Record<ReceiveLocationType, ReceiveLocationType> = {
  INTERNAL_TRANSFER: 'INTERNAL_TRANSFER',
  CUSTOMER_DELIVERIES: 'CUSTOMER_DELIVERIES',
  TRANSSHIPMENT_POINTS: 'TRANSSHIPMENT_POINTS',
  CUSTOMER_ADDRESS: 'CUSTOMER_ADDRESS',
  CONVOY: 'CONVOY'
};

export const RECEIVE_MOVEMENT_TYPE: Record<Uppercase<ReceiveMovementType>, ReceiveMovementType> = {
  INTERNAL_TRANSFERS: 'INTERNAL_TRANSFERS',
  CUSTOMER_DELIVERIES: 'CUSTOMER_DELIVERIES'
};

/** Render order for the movement-type accordions. */
export const RECEIVE_MOVEMENT_ORDER: ReceiveMovementType[] = [
  RECEIVE_MOVEMENT_TYPE.INTERNAL_TRANSFERS,
  RECEIVE_MOVEMENT_TYPE.CUSTOMER_DELIVERIES
];

/**
 * Which `locationType` each movement-type accordion requests, in card render
 * order. One card = one `GET /locations?locationType=…` call. Replaces the v1
 * arrangement where the category had to be scraped out of `groupName`.
 */
export const RECEIVE_MOVEMENT_LOCATION_TYPES: Record<ReceiveMovementType, ReceiveLocationType[]> = {
  INTERNAL_TRANSFERS: [
    RECEIVE_LOCATION_TYPE.INTERNAL_TRANSFER,
    RECEIVE_LOCATION_TYPE.TRANSSHIPMENT_POINTS,
    RECEIVE_LOCATION_TYPE.CUSTOMER_ADDRESS,
    RECEIVE_LOCATION_TYPE.CONVOY
  ],
  CUSTOMER_DELIVERIES: [RECEIVE_LOCATION_TYPE.CUSTOMER_DELIVERIES]
};

/**
 * `groupByDate` per movement type: Customer Deliveries keeps the backend's date
 * level (location → date → consignment), every other type is flat
 * (location → consignment). Preserves the earlier follow-up decision now that the
 * grouping is a request flag rather than a per-endpoint response shape.
 */
export const RECEIVE_GROUP_BY_DATE: Record<ReceiveMovementType, boolean> = {
  INTERNAL_TRANSFERS: false,
  CUSTOMER_DELIVERIES: true
};

/** Endpoint path segments appended to the `/api/v2/receive` base. */
export const RECEIVE_ENDPOINT_SEGMENTS = {
  LOCATIONS: '/locations',
  EXPORT: '/export'
} as const;

/** `GET /locations/{locationId}/shipments` — `locationId` is a composite `siteId~id`. */
export const receiveLocationShipmentsSegment = (locationId: string): `/${string}` =>
  `/locations/${encodeURIComponent(locationId)}/shipments`;

/** `GET /shipments/{shipmentId}/balances` — per-line incoming balances. */
export const receiveShipmentBalancesSegment = (shipmentId: number): `/${string}` => `/shipments/${shipmentId}/balances`;

/** `GET /shipments/{shipmentId}/confirm-context` — everything the receipt modal needs. */
export const receiveConfirmContextSegment = (shipmentId: string): `/${string}` =>
  `/shipments/${encodeURIComponent(shipmentId)}/confirm-context`;

/** `POST /shipments/{shipmentId}/confirm-arrival` — records the receipt. */
export const receiveConfirmArrivalSegment = (shipmentId: string): `/${string}` =>
  `/shipments/${encodeURIComponent(shipmentId)}/confirm-arrival`;

/** `GET /convoys/{convoyId}/confirm-context` — every truck of a convoy awaiting receipt. */
export const receiveConvoyContextSegment = (convoyId: string): `/${string}` =>
  `/convoys/${encodeURIComponent(convoyId)}/confirm-context`;

/** `POST /convoys/{convoyId}/confirm-arrival` — books the whole convoy in one transaction. */
export const receiveConvoyConfirmSegment = (convoyId: string): `/${string}` =>
  `/convoys/${encodeURIComponent(convoyId)}/confirm-arrival`;

/**
 * `GET /shipments/{shipmentId}/attachments/{attachmentId}` — the file bytes.
 *
 * `shipmentId` is the attachment's own `shipmentId`, which after a partial
 * receive is the receipt rather than the shipment being received.
 */
export const receiveAttachmentSegment = (shipmentId: string, attachmentId: number): `/${string}` =>
  `/shipments/${encodeURIComponent(shipmentId)}/attachments/${attachmentId}`;

/** `POST /shipments/{shipmentId}/attachments` — documents for an already booked receipt. */
export const receiveShipmentAttachmentsSegment = (shipmentId: string): `/${string}` =>
  `/shipments/${encodeURIComponent(shipmentId)}/attachments`;

/** Receive and Release: the write, the prefill again, and the free-text suggestions. */
export const RECEIVE_RELEASE_SEGMENTS = {
  RELEASE: '/release',
  CONTEXT: '/release-context',
  LOOKUPS: '/release-lookups'
} as const;

/** Separator of a RITA composite key hash, `siteId~id`. */
export const RECEIVE_COMPOSITE_ID_SEPARATOR = '~';

/**
 * Builds the composite key hash the confirm endpoints expect in their paths.
 *
 * The two halves of the Receive API disagree on the format: the dashboard hands
 * out `consignments[].shipmentId` as a bare integer (and its own
 * `/shipments/{id}/balances` takes it that way), while the confirm endpoints
 * document every path id as `siteId~id` and reject a bare integer with `400`.
 * This is the single place that bridges them — values that already carry the
 * separator pass through untouched, so a backend that starts returning
 * composites needs no change here.
 */
export const toReceiveCompositeId = (siteId: number | undefined, id: number | string): string => {
  const raw = String(id);
  if (raw.includes(RECEIVE_COMPOSITE_ID_SEPARATOR) || siteId == null) return raw;
  return `${siteId}${RECEIVE_COMPOSITE_ID_SEPARATOR}${raw}`;
};

/**
 * Dangerous-goods compatibility ratings that produce a warning. `S` (separated
 * from — the mildest rating) produces none, and a missing matrix row means
 * compatible, so those never reach the client.
 */
/** `scope` of confirm-context / confirm-arrival — see `ReceiveConfirmScope`. */
export const RECEIVE_CONFIRM_SCOPE = {
  SHIPMENT: 'SHIPMENT',
  WAYBILL: 'WAYBILL'
} as const satisfies Record<string, ReceiveConfirmScope>;

export const RECEIVE_WARNING_SEVERITY = {
  /** Must be segregated — the most serious rating. */
  SEGREGATED: 'X',
  /** Away from. */
  AWAY_FROM: 'O'
} as const;

/** i18n key per card label. Customer Deliveries has a single card, so it reuses its movement label. */
export const RECEIVE_LOCATION_TYPE_LABEL_KEYS: Record<ReceiveLocationType, string> = {
  INTERNAL_TRANSFER: 'locationType.W',
  TRANSSHIPMENT_POINTS: 'locationType.P',
  CUSTOMER_ADDRESS: 'locationType.C',
  CONVOY: 'dispatch.dispatchedConvoys.label',
  CUSTOMER_DELIVERIES: 'receive.customerconsignments.desc'
};

/** i18n key per movement-type label. */
export const RECEIVE_MOVEMENT_LABEL_KEYS: Record<ReceiveMovementType, string> = {
  INTERNAL_TRANSFERS: 'receive.internalconsignments.desc',
  CUSTOMER_DELIVERIES: 'receive.customerconsignments.desc'
};
